/**
 * A country flag, served by us.
 *
 * Flags used to come from two outside websites. The country data points at
 * flagcdn.com, and four places carried a hardcoded fallback to
 * worldometers.info -- with a double slash in the path, so it 404'd on every
 * page that fell back to it.
 *
 * Neither is ours. A customer-facing page should not depend on a third party's
 * uptime, URL structure or willingness to keep serving us, least of all for
 * decoration. The images now ship with the app: 249 flags, under a megabyte
 * in total, fetched once at build time and never at runtime.
 *
 * An unknown or missing code gets a neutral placeholder rather than a broken
 * image icon.
 */
const base = process.env.PUBLIC_URL || "";

/*
 * The flags we actually have a file for.
 *
 * A well-formed code was previously enough to build a path, so flagUrl("zw")
 * returned /flags/zw.png -- which does not exist. Zimbabwe 404'd, the global
 * image fallback swapped in the "No image" placeholder, and a country picker
 * showed a broken-looking entry.
 *
 * Checking the code is two letters only proves it is the right SHAPE. This
 * list is what we can actually serve, so an unknown one gets the neutral
 * placeholder, which is what the placeholder was there for.
 *
 * 249 flags. Regenerate from `ls public/flags` if the set changes.
 */
const AVAILABLE = new Set([
  "ad", "ae", "af", "ag", "ai", "al", "am", "ao", "aq", "ar", "as", "at",
  "au", "aw", "ax", "az", "ba", "bb", "bd", "be", "bf", "bg", "bh", "bi",
  "bj", "bl", "bm", "bn", "bo", "bq", "br", "bs", "bt", "bv", "bw", "by",
  "bz", "ca", "cc", "cd", "cf", "cg", "ch", "ci", "ck", "cl", "cm", "cn",
  "co", "cr", "cu", "cv", "cw", "cx", "cy", "cz", "de", "dj", "dk", "dm",
  "do", "dz", "ec", "ee", "eg", "eh", "er", "es", "et", "fi", "fj", "fk",
  "fm", "fo", "fr", "ga", "gb", "gd", "ge", "gf", "gg", "gh", "gi", "gl",
  "gm", "gn", "gp", "gq", "gr", "gs", "gt", "gu", "gw", "gy", "hk", "hm",
  "hn", "hr", "ht", "hu", "id", "ie", "il", "im", "in", "io", "iq", "ir",
  "is", "it", "je", "jm", "jo", "jp", "ke", "kg", "kh", "ki", "km", "kn",
  "kp", "kr", "kw", "ky", "kz", "la", "lb", "lc", "li", "lk", "lr", "ls",
  "lt", "lu", "lv", "ly", "ma", "mc", "md", "me", "mf", "mg", "mh", "mk",
  "ml", "mm", "mn", "mo", "mp", "mq", "mr", "ms", "mt", "mu", "mv", "mw",
  "mx", "my", "mz", "na", "nc", "ne", "nf", "ng", "ni", "nl", "no", "np",
  "nr", "nu", "nz", "om", "pa", "pe", "pf", "pg", "ph", "pk", "pl", "pm",
  "pn", "pr", "ps", "pt", "pw", "py", "qa", "re", "ro", "rs", "ru", "rw",
  "sa", "sb", "sc", "sd", "se", "sg", "sh", "si", "sj", "sk", "sl", "sm",
  "sn", "so", "sr", "ss", "st", "sv", "sx", "sy", "sz", "tc", "td", "tf",
  "tg", "th", "tj", "tk", "tl", "tm", "tn", "to", "tr", "tt", "tv", "tw",
  "tz", "ua", "ug", "um", "us", "uy", "uz", "va", "vc", "ve", "vg", "vi",
  "vn", "vu", "wf", "ws", "xk", "ye", "yt", "za", "zm",
]);

export const flagUrl = (code?: string | null): string => {
  const cc = String(code || "").trim().toLowerCase();
  return AVAILABLE.has(cc)
    ? `${base}/flags/${cc}.png`
    : `${base}/flags/placeholder.png`;
};

export default flagUrl;
